// The single redaction rule table (plan 11.6.2) and the marker grammar shared with the web app.
// Three treatments: delete (unrecoverable), consistent token (same value -> same token inside one
// conversation, so the flow stays readable) and marker ([REDACTED:kind]); long blobs are omitted.
/**
 * Version of the rule table. Every stored text records the version it was redacted with.
 * m1 = r04 masking.ts; m2 = this table (connection-token prefixes, consistent tokens, accounts, URL credentials);
 * m3 = m2 without masking numbers after token keys (token counts and limits such as max_output_tokens, D-448).
 */
export const REDACTION_VERSION = 'm3';
export const REDACT_KINDS = [
    'private_key',
    'jwt',
    'auth_token',
    'api_key',
    'connection_token',
    'service_secret',
    'url_credential',
    'db_password',
    'env_value',
    'password',
    'rrn',
    'phone',
    'card',
    'account',
    'email',
    'base64',
];
export const TREATMENTS = ['marker', 'delete', 'token', 'omit'];
export const TOKEN_KINDS = ['email', 'phone', 'card', 'account'];
/** Rules in the order they run: secrets first so later, broader patterns never see a secret's prefix. */
export const REDACTION_RULES = [
    { kind: 'private_key', category: 'secret', treatment: 'marker', source: ['r04', 'aiax', 'r03'] },
    { kind: 'jwt', category: 'secret', treatment: 'marker', source: ['r04', 'aiax', 'r03'] },
    { kind: 'auth_token', category: 'secret', treatment: 'marker', source: ['r04', 'aiax', 'r03'] },
    { kind: 'api_key', category: 'secret', treatment: 'marker', source: ['r04', 'aiax', 'r03'] },
    { kind: 'connection_token', category: 'secret', treatment: 'marker', source: ['plan'] },
    { kind: 'service_secret', category: 'secret', treatment: 'marker', source: ['r04'] },
    { kind: 'db_password', category: 'assignment', treatment: 'marker', source: ['r04', 'aiax'] },
    { kind: 'url_credential', category: 'secret', treatment: 'marker', source: ['r03'] },
    { kind: 'env_value', category: 'assignment', treatment: 'marker', source: ['r04'] },
    { kind: 'password', category: 'assignment', treatment: 'marker', source: ['r04', 'aiax', 'r03'] },
    { kind: 'rrn', category: 'national_id', treatment: 'delete', source: ['r04'] },
    { kind: 'phone', category: 'contact_finance', treatment: 'token', source: ['aiax', 'r04'] },
    { kind: 'card', category: 'contact_finance', treatment: 'token', source: ['aiax', 'r04'] },
    { kind: 'account', category: 'contact_finance', treatment: 'token', source: ['aiax'] },
    { kind: 'email', category: 'contact_finance', treatment: 'token', source: ['aiax', 'r04'] },
    { kind: 'base64', category: 'blob', treatment: 'omit', source: ['r04'] },
];
/** Fields the table applies to (plan 11.6.4). */
export const REDACTED_FIELDS = [
    'prompt',
    'response',
    'tool_input',
    'tool_output',
    'error_message',
    'attribute_text',
    'list_title',
    'gateway_text',
    'operational_log',
];
export const DEFAULT_TREATMENT = Object.fromEntries(REDACTION_RULES.map((r) => [r.kind, r.treatment]));
/**
 * Every marker form: [REDACTED:kind], [DELETED:kind], [OMITTED:kind:bytes], [EMAIL_3f9a1c20] / [EMAIL_1].
 * The web app uses it to highlight replaced spans; redaction uses it to never re-mask its own output.
 */
export const MARKER_SOURCE = String.raw `\[(?:(?:REDACTED|DELETED):[a-z_]+|OMITTED:[a-z0-9_]+:\d+|(?:EMAIL|PHONE|CARD|ACCOUNT)_[0-9a-f]{1,16})\]`;
export const markerPattern = (flags = 'g') => new RegExp(MARKER_SOURCE, flags);
const WHOLE_MARKER = new RegExp(`^${MARKER_SOURCE}$`);
export const isMarker = (s) => WHOLE_MARKER.test(s.trim());
/** True when the value already contains a marker (a span this engine produced). */
export const containsMarker = (s) => markerPattern('').test(s);
/** Browser-safe marker label; never imports the crypto/tokenizer implementation. */
export function markerKind(s) {
    if (!WHOLE_MARKER.test(s))
        return null;
    const inside = s.slice(1, -1);
    return inside.includes(':') ? inside.split(':')[1] : inside.slice(0, inside.indexOf('_')).toLowerCase();
}
//# sourceMappingURL=rules.js.map